import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '~/lib/utils';
import { Slot } from '@radix-ui/react-slot';

const sharedDefaults =
  'whitespace-nowrap rounded cursor-pointer ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-[transform,background-color,color] [&:not(:disabled)]:active:translate-y-0.5 font-aspekta rounded-full aspect-1';
const sharedDisabled = 'disabled:text-gray-400 disabled:cursor-not-allowed';

const iconButtonVariants = cva(`${sharedDefaults} ${sharedDisabled}`, {
  variants: {
    variant: {
      default:
        'bg-primary-button text-primary-button-foreground [&:not(:disabled)]:hover:bg-primary-button-hover [&:not(:disabled)]:focus:bg-primary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      destructive:
        'bg-destructive text-white [&:not(:disabled)]:hover:bg-destructive-hover [&:not(:disabled)]:focus:bg-destructive-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      success:
        'bg-secondary text-black [&:not(:disabled)]:hover:bg-secondary-hover [&:not(:disabled)]:focus:bg-secondary-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      secondary:
        'border bg-secondary-button border-secondary-button-border [&:not(:disabled)]:hover:bg-secondary-button-hover [&:not(:disabled)]:focus:bg-secondary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      tertiary:
        'bg-tertiary-button text-tertiary-button-foreground [&:not(:disabled)]:hover:bg-tertiary-button-hover [&:not(:disabled)]:focus:bg-tertiary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      outline:
        'border bg-secondary-button border-secondary-button-border [&:not(:disabled)]:hover:bg-secondary-button-hover [&:not(:disabled)]:focus:bg-secondary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      ghost:
        'bg-transparent text-foreground [&:not(:disabled)]:hover:bg-primary-button [&:not(:disabled)]:focus:bg-primary [&:not(:disabled)]:hover:text-primary-button-foreground [&:not(:disabled)]:focus:text-primary-button-foreground',
      link: 'text-foreground underline-offset-4 underline [&:not(:disabled)]:hover:no-underline [&:not(:disabled)]:focus:no-underline w-auto h-auto',
    },
    size: {
      default: 'w-12 h-12',
      xs: 'text-xs w-6 h-6',
      sm: 'text-sm w-8 h-8',
      lg: 'w-14 h-14',
    },
    font: {
      primary: 'font-aspekta',
      secondary: 'font-spaceMono',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

// @TODO:
// Text buttons shouldn't care about size shouldn't care about inline elements
// Icon's shouldn't should be resizable, so icon should be its own prop.
export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
  hasIcon?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, font = 'primary', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // We need to make sure typescript sets a type using HTMLButtonElement props since its not optional
    const type = Comp === 'button' ? 'button' : undefined;

    // Display Link Buttons as inline elements
    const displayType = variant === 'link' ? 'inline' : 'inline-flex items-center justify-center';

    return (
      <Comp
        className={cn(
          iconButtonVariants({
            variant,
            size,
            font,
          }),
          displayType,
          {
            'p-0': variant === 'link',
          },
          className,
        )}
        ref={ref}
        type={type}
        {...props}
      />
    );
  },
);
IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
